import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Factory, MapPin, Eye, Globe, Sparkles, Handshake, CheckCircle } from "lucide-react"; // Changed ArrowRight to more descriptive icons
import { Helmet } from "react-helmet";

// Import images
import bannerImg from "../assets/cocopeat23.webp";
import facilityImg1 from "../assets/about/land1.jpeg";
import facilityImg2 from "../assets/about/about5.jpeg";
import facilityImg3 from "../assets/about/about1.jpeg";
import facilityImg4 from "../assets/about/land4.jpeg";
import land2 from "../assets/about/land2.jpeg";
import facilityImg5 from "../assets/about/about3.jpeg";
import facilityImg6 from "../assets/about/process12.jpg";

const AboutUs = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000, // Slightly reduced duration for snappier animations
      easing: "ease-in-out",
      once: true,
      mirror: false, // Only animate once
    });
  }, []);

  const facilities = [
    {
      image: facilityImg1,
      title: "5 Acres Exclusive Production Location",
      icon: <Factory className="w-12 h-12 text-green-400 group-hover:text-green-600 transition-colors duration-300" />,
    },
    {
      image: facilityImg2,
      title: "Comprehensive Pith Collections",
      icon: <Globe className="w-12 h-12 text-blue-400 group-hover:text-blue-600 transition-colors duration-300" />,
    },
    {
      image: facilityImg3,
      title: "Advanced Fiber Mill",
      icon: <MapPin className="w-12 h-12 text-purple-400 group-hover:text-purple-600 transition-colors duration-300" />,
    },
    {
      image: facilityImg4,
      title: "80,000 sq. ft. Drying Yard",
      icon: <Eye className="w-12 h-12 text-indigo-400 group-hover:text-indigo-600 transition-colors duration-300" />,
    },
    {
      image: facilityImg5,
      title: "Strategic Bunkers for Processing",
      icon: <Sparkles className="w-12 h-12 text-yellow-400 group-hover:text-yellow-600 transition-colors duration-300" />, // Changed icon
    },
    {
      image: facilityImg6,
      title: "State-of-the-Art Drying Unit",
      icon: <Factory className="w-12 h-12 text-green-400 group-hover:text-green-600 transition-colors duration-300" />,
    },
  ];

  return (
    <div className="bg-gray-50 font-sans mt-10">
      <Helmet>
        <title>About Tirumala Cocopeat | Best Cocopeat Exporters in Andhra Pradesh, Anantapur</title>
        <meta name="description" content="Learn about Tirumala Cocopeat, the leading manufacturer and exporter of premium cocopeat in Andhra Pradesh, Anantapur. Discover our production facilities, quality standards, and commitment to sustainable agriculture and global exports." />
        <meta name="keywords" content="Tirumala Cocopeat, about cocopeat, best cocopeat exporters, cocopeat Anantapur, cocopeat Andhra Pradesh, cocopeat manufacturer, cocopeat supplier, cocopeat exporter, premium cocopeat, sustainable cocopeat, organic cocopeat, cocopeat production, cocopeat factory, cocopeat India, global cocopeat export, best cocopeat company, cocopeat for agriculture, cocopeat for horticulture, cocopeat for hydroponics, cocopeat for export, cocopeat quality, cocopeat facilities, cocopeat business, best cocopeat in Andhra Pradesh, best cocopeat in Anantapur, best cocopeat exporters, exports" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="About Tirumala Cocopeat | Best Cocopeat Exporters in Andhra Pradesh, Anantapur" />
        <meta property="og:description" content="Learn about Tirumala Cocopeat, the leading manufacturer and exporter of premium cocopeat in Andhra Pradesh, Anantapur. Discover our production facilities, quality standards, and commitment to sustainable agriculture and global exports." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tirumalacocopeat.vercel.app/aboutus" />
      </Helmet>

      {/* Banner Section */}
      <div
        className="relative w-full h-[300px] md:h-[400px] lg:h-[500px] bg-cover bg-center"
        style={{ backgroundImage: `url(${bannerImg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-green-900/60 to-black/70 flex items-center justify-center">
          <div className="text-center px-4 max-w-4xl mx-auto">
            <h1
              className="text-white text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight tracking-tight mb-4"
              data-aos="fade-up"
            >
              About Tirumala Cocopeat
            </h1>
            <p
              className="text-gray-200 text-lg sm:text-xl md:text-2xl"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Your Partner in Sustainable Agricultural Solutions
            </p>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20"> {/* Increased section spacing */}

        {/* Company Story */}
        <section className="flex flex-col md:flex-row gap-12 items-center">
          <div className="md:w-1/2" data-aos="fade-right">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              Our Journey: From Tradition to Innovation
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Founded in 2015 and officially established as{" "}
              <strong className="text-green-700">Tirumala Cocopeat Pvt Ltd</strong> in 2019, we are proudly located
              in the heart of Anantapur, a region synonymous with quality coconut produce.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our roots run deep: with over 40 years of family experience in coconut farming, we possess an unparalleled understanding of the raw material. This profound knowledge empowers us to transform raw coconut husks into superior, high-quality agricultural substrates, championing sustainable practices and delivering excellence to growers worldwide.
            </p>
          </div>
          <div
            className="md:w-1/2 bg-white shadow-xl rounded-2xl overflow-hidden transform hover:scale-102 transition-transform duration-500 ease-in-out"
            data-aos="fade-right" // Changed to fade-left for better flow
          >
            <img
              src={land2}
              alt="Tirumala Cocopeat Facility"
              className="w-full h-72 md:h-96 object-cover object-center rounded-2xl"
            />
          </div>
        </section>

        {/* Facilities Grid */}
        <section className="py-12"> {/* Added vertical padding */}
          <h2
            className="text-3xl sm:text-4xl font-bold text-center mb-16 text-gray-900"
            data-aos="fade-up"
          >
            Our State-of-the-Art Facilities
          </h2>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            {facilities.map((facility, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl shadow-lg overflow-hidden
                           transform transition-all duration-500 ease-in-out hover:shadow-2xl hover:scale-102 cursor-pointer"
              >
                <img
                  src={facility.image}
                  alt={facility.title}
                  className="w-full h-64 object-cover object-center rounded-t-2xl
                             transform transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent flex flex-col justify-end p-6">
                  <div className="mb-3 transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                     {facility.icon} {/* Icon inside the overlay */}
                  </div>
                  <h3 className="font-bold text-xl text-white group-hover:text-green-300 transition-colors duration-300 leading-tight">
                    {facility.title}
                  </h3>
                  <p className="text-gray-300 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-2">
                    Explore our modern infrastructure.
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Vision and Why Choose Us */}
        <section className="flex flex-col md:flex-row gap-10"> {/* Changed to flex-row for larger screens */}
          <div className="md:w-1/2 p-8 rounded-2xl shadow-xl bg-gradient-to-br from-green-50 to-white transform hover:translate-y-[-5px] transition-transform duration-300" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Our Vision
            </h2>
            <blockquote className="italic text-xl text-gray-800 leading-relaxed border-l-4 border-green-500 pl-4">
              "To create a distinguished name in specialized coir pith
              manufacturing, delivering assured quality and guaranteed timely
              solutions to our global clientele."
            </blockquote>
          </div>
          <div className="md:w-1/2 p-8 rounded-2xl shadow-xl bg-gradient-to-br from-green-100 to-green-50 transform hover:translate-y-[-5px] transition-transform duration-300" data-aos="fade-up" data-aos-delay="200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why Choose Tirumala Cocopeat?
            </h2>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start"> {/* Use items-start for multiline bullets */}
                <CheckCircle className="mr-3 text-green-700 mt-1 flex-shrink-0" />
                <span>100% Organic, Eco-Friendly, and Sustainable Products.</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-green-700 mt-1 flex-shrink-0" />
                <span>Adherence to rigorous High-Quality Standards at every stage of production.</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-green-700 mt-1 flex-shrink-0" />
                <span>Benefit from an Expert Team with over 40 years of invaluable experience in coconut farming and coir processing.</span>
              </li>
              <li className="flex items-start">
                <Handshake className="mr-3 text-green-700 mt-1 flex-shrink-0" /> {/* New icon for partnership */}
                <span>Dedicated to building lasting partnerships through reliable service and consistent product excellence.</span>
              </li>
            </ul>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AboutUs;